import React, { Component } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
} from "react-simple-maps";
import { geoMercator } from 'd3-geo';
import { feature as topojsonFeature } from 'topojson-client';
import { UncontrolledTooltip } from 'reactstrap';

class Map extends Component {
  constructor(props) {
    super(props);
    this.state = {
      province: null,
      featureCollection: null,
      geographies: [],
      mapWidth: 760,
      mapHeight: 420
    };

    this.containerRef = React.createRef();
    this.handleResize = this.handleResize.bind(this);
    this.handleDistrictClick.bind(this);
    this.checkIfSelected.bind(this);
  }

  componentDidMount() {
    this.getProvinceAndGeo();
    this.setupResizeTracking();
  }

  componentDidUpdate(prevProps) {
    const { prefecture } = this.props;

    if(prefecture !== prevProps.prefecture) {
      this.getProvinceAndGeo();
    }
  }

  componentWillUnmount() {
    if(this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  setupResizeTracking() {
    this.handleResize();

    if(typeof ResizeObserver !== 'undefined' && this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.containerRef.current);
    } else {
      window.addEventListener('resize', this.handleResize);
    }
  }

  handleResize() {
    const node = this.containerRef.current;
    if(!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const nextWidth = Math.max(320, Math.round(rect.width || node.clientWidth || 760));
    const rawHeight = Math.round(rect.height || node.clientHeight || nextWidth * 0.56);
    const nextHeight = Math.max(300, rawHeight);

    if(nextWidth !== this.state.mapWidth || nextHeight !== this.state.mapHeight) {
      this.setState({
        mapWidth: nextWidth,
        mapHeight: nextHeight
      });
    }
  }

  getProvinceAndGeo() {
    const { prefecture } = this.props;

    if(prefecture) {
      fetch(prefecture.relationships.province.links.related).then((resp) => resp.json())
        .then((respJson) => {
          const province = respJson.data;
          fetch('/geographies/prefectures/' + province.attributes.name + '/' + prefecture.attributes.name + '.json').then((resp) => resp.json())
            .then((topology) => {
              const objectName = Object.keys(topology.objects)[0];
              const featureCollection = topojsonFeature(topology, topology.objects[objectName]);

              this.setState({
                province: province,
                featureCollection: featureCollection,
                geographies: featureCollection.features
              }, () => {
                window.requestAnimationFrame(this.handleResize);
              });
            });
        });
    }
  }

  buildProjection(width, height) {
    const { featureCollection } = this.state;
    const projection = geoMercator();

    if(featureCollection && featureCollection.features && featureCollection.features.length > 0) {
      const inset = Math.max(16, Math.round(Math.min(width, height) * 0.045));
      projection.fitExtent(
        [
          [inset, inset],
          [width - inset, height - inset]
        ],
        featureCollection
      );
    }

    return projection;
  }

  handleDistrictClick(geoObj) {
    const { districts } = this.props;

    districts.forEach((district) => {
      if(geoObj.chineseName === district.attributes['chinese-name']) {
        this.props.updatePrefecture({
          displayName: district.attributes.name,
          displayChineseName: district.attributes['chinese-name'],
          currentDistricts: [district]
        });
      }
    });
  }

  checkIfSelected(geoChineseName) {
    const { currentDistricts } = this.props;
    let selected = false;

    currentDistricts.forEach((district) => {
      if(district.attributes['chinese-name'] === geoChineseName) {
        selected = true;
      }
    });

    return selected;
  }

  getColor(geoChineseName) {
    const { districts } = this.props;
    let selected = false;
    let type = null;
    let color = '#ECEFF1';

    selected = this.checkIfSelected(geoChineseName);

    districts.forEach((district) => {
      if(district.attributes['chinese-name'] === geoChineseName) {
        type = district.attributes['subarea-type'];
      }
    });

    if(type === 'Core District') {
      color = selected ? '#DC3545' : '#ebb0b4';
    } else if(type === 'Suburban District') {
      color = selected ? '#FFC107' : '#ffe5a6';
    } else if(type === 'City') {
      color = selected ? '#29A645' : '#9ec5a2';
    } else if(type) {
      color = selected ? '#007BFF' : '#9dc7ff';
    }

    return color;
  }

  getBusinessCount(geoObj) {
    const { businessTotals } = this.props;
    let count = null;

    businessTotals.forEach((total) => {
      if(total.name === geoObj.properties.name) {
        count = `${total.value} (${total.year})`;
      }
    });

    return count;
  }

  render() {
    const { province, geographies, mapWidth, mapHeight } = this.state;
    const { businessTotals } = this.props;

    if(province && geographies.length > 0) {
      return (
        <div className="w-100 my-0 mx-auto pref-district-map" ref={this.containerRef}>
          <ComposableMap
            projection={(width, height) => this.buildProjection(width, height)}
            width={mapWidth}
            height={mapHeight}
            style={{
              width: '100%',
              height: '100%'
            }}>
            <Geographies geography={geographies} disableOptimization>
              {(loadedGeographies, projection) => loadedGeographies.map((geography, i) => geography.id !== 'ATA' && (
                geography.properties.notAvailable ?
                  <Geography
                    key={i}
                    id={'geo-' + i}
                    geography={geography}
                    projection={projection}
                    style={{
                      default: {
                        fill: '#ECEFF1',
                        stroke: '#343A40',
                        strokeWidth: 0.75,
                        opacity: 0.3,
                        outline: 'none',
                      },
                      hover: {
                        fill: '#ECEFF1',
                        stroke: '#343A40',
                        strokeWidth: 0.75,
                        opacity: 0.3,
                        outline: 'none',
                      },
                      pressed: {
                        fill: '#ECEFF1',
                        stroke: '#343A40',
                        strokeWidth: 0.75,
                        opacity: 0.3,
                        outline: 'none',
                      },
                    }}>
                    <UncontrolledTooltip placement="bottom" delay={0} target={'geo-' + i}>
                      {geography.properties.name} (Coming Soon)
                    </UncontrolledTooltip>
                  </Geography>
                :
                  <Geography
                    key={i}
                    id={'geo-' + i}
                    geography={geography}
                    projection={projection}
                    onClick={(geo) => this.handleDistrictClick(geo.properties)}
                    style={{
                      default: {
                        fill: this.getColor(geography.properties.chineseName),
                        stroke: '#343A40',
                        strokeWidth: 0.75,
                        outline: 'none',
                      },
                      hover: {
                        fill: '#215796',
                        stroke: '#343A40',
                        strokeWidth: 0.75,
                        outline: 'none',
                      },
                      pressed: {
                        fill: '#e25241',
                        stroke: '#343A40',
                        strokeWidth: 0.75,
                        outline: 'none',
                      },
                    }}>
                    <UncontrolledTooltip placement="bottom" delay={0} target={'geo-' + i}>
                      {geography.properties.name}
                    </UncontrolledTooltip>
                    {businessTotals.length > 0 && this.getBusinessCount(geography) !== null && (
                      <UncontrolledTooltip placement="top" delay={0} target={'geo-' + i}>
                        {this.getBusinessCount(geography)}
                      </UncontrolledTooltip>
                    )}
                  </Geography>
              ))}
            </Geographies>
          </ComposableMap>
        </div>
      );
    }

    return <div className="pref-district-map" ref={this.containerRef} />;
  }
}

export default Map;